/**
 * Farm Management Redux Slice
 * 
 * Manages state for farms, fields, irrigation sectors, pump stations,
 * and weather stations in Redux store.
 */

import { emptyArray, emptyRecord } from '@/store/emptyValues';
import { createSlice, createAsyncThunk, createSelector, PayloadAction } from '@reduxjs/toolkit';
import type {
  Farm,
  FarmCreate,
  FarmOption,
  FarmUpdate,
  FarmStatsResponse,
  WeatherDataResponse,
  Field,
  FieldCreate,
  FieldUpdate,
  FieldListResponse,
  IrrigationSector,
  IrrigationSectorCreate,
  IrrigationSectorUpdate,
  IrrigationSectorListResponse,
  PumpStation,
  PumpStationCreate,
  PumpStationUpdate,
  PumpStationListResponse,
  WeatherStation,
  WeatherStationCreate,
  WeatherStationUpdate,
  WeatherStationListResponse,
  IrrigationZone,
  IrrigationZoneCreate,
  IrrigationZoneUpdate,
  ZoneSectorMembership,
  IrrigationZoneWithSectors,
  IrrigationZoneListResponse,
  GeoImportResult,
} from '@/types/farms';
import farmApi from '@/services/farmApi';
import { isInFlight, markInFlight, clearInFlight } from '@/store/inFlight';
import irrigationZonesApi from '@/services/irrigationZonesApi';

// ============================================================================
// Farm Slice
// ============================================================================

export const WEATHER_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes – must match backend cache_minutes

interface FarmState {
  farms: Farm[];
  // Lightweight list for dropdowns/selectors. Sourced from GET /farms/list,
  // unpaginated, no user_roles. Fetched separately from `farms` so the
  // dropdown stays cheap even when full-detail views aren't loaded.
  farmList: FarmOption[];
  farmListLoading: boolean;
  selectedFarm: Farm | null;
  loading: boolean;
  // True once the first fetchFarms request has settled (fulfilled or
  // rejected). `loading` starts false, so gating UI on `!loading` alone
  // still fires before the first fetch is even dispatched.
  farmsLoaded: boolean;
  error: string | null;
  stats: FarmStatsResponse | null;
  weatherData: Record<string, WeatherDataResponse>; // farmId -> weather data
  weatherCachedAt: Record<string, string>; // farmId -> ISO timestamp of last successful fetch
  weatherLoading: Record<string, boolean>; // farmId -> loading state
}

const initialFarmState: FarmState = {
  farms: [],
  farmList: [],
  farmListLoading: false,
  selectedFarm: null,
  loading: false,
  farmsLoaded: false,
  error: null,
  stats: null,
  weatherData: {},
  weatherCachedAt: {},
  weatherLoading: {},
};

// Async Thunks
// Default per_page is large enough to fit all farms in a single request
// for any tenant we have today; the API caps it at 500. Callers can
// override if they ever need true pagination.
export const fetchFarms = createAsyncThunk(
  'farms/fetchFarms',
  async (params?: { page?: number; per_page?: number; is_active?: boolean }) => {
    const response = await farmApi.getFarms({ per_page: 500, ...params });
    return response;
  }
);

/**
 * Fetch the lightweight farm list used by the dropdown/selectors. Hits
 * GET /farms/list which is unpaginated and skips user_roles — much
 * cheaper than fetchFarms() and never truncated.
 */
export const fetchFarmList = createAsyncThunk(
  'farms/fetchFarmList',
  async (params?: { include_inactive?: boolean }) => {
    return await farmApi.getFarmOptions(params);
  }
);

export const createFarm = createAsyncThunk(
  'farms/createFarm',
  async (data: FarmCreate) => {
    const response = await farmApi.createFarm(data);
    return response;
  }
);

export const updateFarm = createAsyncThunk(
  'farms/updateFarm',
  async ({ id, data }: { id: string; data: FarmUpdate }) => {
    const response = await farmApi.updateFarm(id, data);
    return response;
  }
);

export const deleteFarm = createAsyncThunk(
  'farms/deleteFarm',
  async (id: string) => {
    await farmApi.deleteFarm(id);
    return id;
  }
);

export const discardDraftFarm = createAsyncThunk(
  'farms/discardDraftFarm',
  async (id: string) => {
    await farmApi.discardDraftFarm(id);
    return id;
  }
);

export const fetchFarmStats = createAsyncThunk(
  'farms/fetchFarmStats',
  async () => {
    const response = await farmApi.getFarmStats();
    return response;
  }
);

export const fetchFarmWeather = createAsyncThunk(
  'farms/fetchFarmWeather',
  async (farmId: string) => {
    const response = await farmApi.getFarmWeather(farmId);
    return { farmId, data: response };
  },
  {
    // Skip the API call if we already have fresh data (< WEATHER_CACHE_TTL_MS old).
    // Also refetch when the cached payload is empty / missing the core
    // measurement fields — otherwise a previously broken response (e.g.
    // backend bug filed during an outage) stays pinned in localStorage for
    // the full TTL even after the backend recovers.
    condition: (farmId: string, { getState }) => {
      const state = getState() as { farms: FarmState };
      const cachedAt = state.farms.weatherCachedAt?.[farmId];
      if (!cachedAt) return true; // No cache – fetch
      const cachedPayload = state.farms.weatherData?.[farmId]?.data;
      const cachedLooksEmpty =
        !cachedPayload ||
        (cachedPayload.temperature == null && cachedPayload.humidity == null);
      if (cachedLooksEmpty) return true; // Bad cache – refetch
      const ageMs = Date.now() - new Date(cachedAt).getTime();
      return ageMs >= WEATHER_CACHE_TTL_MS; // Only fetch when stale
    },
  }
);

// Slice
const farmSlice = createSlice({
  name: 'farms',
  initialState: initialFarmState,
  reducers: {
    selectFarm: (state, action: PayloadAction<Farm>) => {
      state.selectedFarm = action.payload;
    },
    clearSelectedFarm: (state) => {
      state.selectedFarm = null;
    },
    clearFarmError: (state) => {
      state.error = null;
    },
    // Invalidate the cached weather entry for a farm so the next
    // ``fetchFarmWeather`` dispatch bypasses the TTL guard in its
    // ``condition``. Used by the WebSocket sensor_update handler to push
    // fresh readings as soon as the bridge writes them.
    invalidateFarmWeather: (state, action: PayloadAction<string>) => {
      const farmId = action.payload;
      if (state.weatherCachedAt[farmId]) {
        delete state.weatherCachedAt[farmId];
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch Farms
    builder
      .addCase(fetchFarms.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFarms.fulfilled, (state, action) => {
        state.loading = false;
        state.farmsLoaded = true;
        // Ensure farms is always an array, fallback to empty array if undefined
        state.farms = Array.isArray(action.payload?.farms) ? action.payload.farms : [];
      })
      .addCase(fetchFarms.rejected, (state, action) => {
        state.loading = false;
        state.farmsLoaded = true;
        state.error = action.error.message || 'Failed to fetch farms';
      });

    // Fetch Farm List (lightweight, for dropdowns)
    builder
      .addCase(fetchFarmList.pending, (state) => {
        state.farmListLoading = true;
      })
      .addCase(fetchFarmList.fulfilled, (state, action) => {
        state.farmListLoading = false;
        state.farmList = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchFarmList.rejected, (state, action) => {
        state.farmListLoading = false;
        state.error = action.error.message || 'Failed to fetch farm list';
      });

    // Create Farm
    builder
      .addCase(createFarm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createFarm.fulfilled, (state, action) => {
        state.loading = false;
        state.farms.push(action.payload);
        const f = action.payload;
        state.farmList.push({
          id: f.id,
          name: f.name,
          timezone: f.timezone,
          latitude: f.latitude,
          longitude: f.longitude,
          is_active: f.is_active,
        });
        state.farmList.sort((a, b) => a.name.localeCompare(b.name));
      })
      .addCase(createFarm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create farm';
      });

    // Update Farm
    builder
      .addCase(updateFarm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateFarm.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.farms.findIndex((f) => f.id === action.payload.id);
        if (index !== -1) {
          state.farms[index] = action.payload;
        }
        if (state.selectedFarm?.id === action.payload.id) {
          state.selectedFarm = action.payload;
        }
        const listIdx = state.farmList.findIndex((f) => f.id === action.payload.id);
        if (listIdx !== -1) {
          const f = action.payload;
          state.farmList[listIdx] = {
            id: f.id,
            name: f.name,
            timezone: f.timezone,
            latitude: f.latitude,
            longitude: f.longitude,
            is_active: f.is_active,
          };
          state.farmList.sort((a, b) => a.name.localeCompare(b.name));
        }
      })
      .addCase(updateFarm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update farm';
      });

    // Delete Farm
    builder
      .addCase(deleteFarm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteFarm.fulfilled, (state, action) => {
        state.loading = false;
        state.farms = state.farms.filter((f) => f.id !== action.payload);
        state.farmList = state.farmList.filter((f) => f.id !== action.payload);
        if (state.selectedFarm?.id === action.payload) {
          state.selectedFarm = null;
        }
      })
      .addCase(deleteFarm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete farm';
      });

    // Discard draft farm (hard delete of an in-progress wizard farm)
    builder
      .addCase(discardDraftFarm.fulfilled, (state, action) => {
        state.farms = state.farms.filter((f) => f.id !== action.payload);
        state.farmList = state.farmList.filter((f) => f.id !== action.payload);
        if (state.selectedFarm?.id === action.payload) {
          state.selectedFarm = null;
        }
      });

    // Fetch Farm Stats
    builder
      .addCase(fetchFarmStats.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFarmStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchFarmStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch farm stats';
      });

    // Fetch Farm Weather
    builder
      .addCase(fetchFarmWeather.pending, (state, action) => {
        state.weatherLoading[action.meta.arg] = true;
      })
      .addCase(fetchFarmWeather.fulfilled, (state, action) => {
        state.weatherLoading[action.payload.farmId] = false;
        state.weatherData[action.payload.farmId] = action.payload.data;
        state.weatherCachedAt[action.payload.farmId] = new Date().toISOString();
      })
      .addCase(fetchFarmWeather.rejected, (state, action) => {
        state.weatherLoading[action.meta.arg] = false;
        state.error = action.error.message || 'Failed to fetch farm weather';
      });
  },
});

export const { selectFarm, clearSelectedFarm, clearFarmError, invalidateFarmWeather } = farmSlice.actions;
export const farmReducer = farmSlice.reducer;

// Selectors
export const selectFarms = (state: { farms: FarmState }) => state.farms.farms;
export const selectFarmList = (state: { farms: FarmState }) => state.farms.farmList;
export const selectFarmListLoading = (state: { farms: FarmState }) => state.farms.farmListLoading;
export const selectSelectedFarm = (state: { farms: FarmState }) => state.farms.selectedFarm;
export const selectFarmLoading = (state: { farms: FarmState }) => state.farms.loading;
export const selectFarmsLoaded = (state: { farms: FarmState }) => state.farms.farmsLoaded;
export const selectFarmError = (state: { farms: FarmState }) => state.farms.error;
export const selectFarmStats = (state: { farms: FarmState }) => state.farms.stats;
export const selectFarmWeatherData = (farmId: string) => (state: { farms: FarmState }) =>
  state.farms.weatherData[farmId];
export const selectFarmWeatherLoading = (farmId: string) => (state: { farms: FarmState }) =>
  state.farms.weatherLoading[farmId] ?? false;
export const selectFarmWeatherCachedAt = (farmId: string) => (state: { farms: FarmState }) =>
  state.farms.weatherCachedAt[farmId] ?? null;

// ============================================================================
// Field Slice
// ============================================================================

interface FieldState {
  fields: Record<string, Field[]>; // farmId -> fields
  selectedField: Field | null;
  loading: boolean;
  error: string | null;
}

const initialFieldState: FieldState = {
  fields: {},
  selectedField: null,
  loading: false,
  error: null,
};

// Async Thunks
export const fetchFields = createAsyncThunk(
  'fields/fetchFields',
  async (params: { farm_id: string; page?: number; size?: number }) => {
    const key = `fields:${params.farm_id}`;
    markInFlight(key);
    try {
      const response = await farmApi.getFields(params);
      return { farmId: params.farm_id, fields: response.items };
    } finally {
      clearInFlight(key);
    }
  },
  {
    condition: (params: { farm_id: string; page?: number; size?: number }) =>
      !isInFlight(`fields:${params.farm_id}`),
  }
);

export const createField = createAsyncThunk(
  'fields/createField',
  async (data: FieldCreate & { pendingBoundary?: string }) => {
    const response = await farmApi.createField(data);
    return { ...response, pendingBoundary: data.pendingBoundary };
  }
);

export const updateField = createAsyncThunk(
  'fields/updateField',
  async ({ id, data }: { id: string; data: FieldUpdate }) => {
    const response = await farmApi.updateField(id, data);
    return response;
  }
);

export const deleteField = createAsyncThunk(
  'fields/deleteField',
  async (id: string) => {
    await farmApi.deleteField(id);
    return id;
  }
);

// Slice
const fieldSlice = createSlice({
  name: 'fields',
  initialState: initialFieldState,
  reducers: {
    selectField: (state, action: PayloadAction<Field>) => {
      state.selectedField = action.payload;
    },
    clearSelectedField: (state) => {
      state.selectedField = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Fields
    builder
      .addCase(fetchFields.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFields.fulfilled, (state, action) => {
        state.loading = false;
        // Transform API response: geometry object → boundary string
        const transformedFields = action.payload.fields.map((field: any) => ({
          ...field,
          // Convert geometry object to boundary string (GeoJSON)
          boundary: field.geometry ? JSON.stringify(field.geometry) : field.boundary,
        }));
        state.fields[action.payload.farmId] = transformedFields;
      })
      .addCase(fetchFields.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch fields';
      });

    // Create Field
    builder
      .addCase(createField.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createField.fulfilled, (state, action) => {
        state.loading = false;
        // Transform API response: geometry object → boundary string (same as fetchFields)
        // Use pendingBoundary as fallback if backend doesn't return geometry/boundary
        const rawField: any = action.payload;
        const pendingBoundary = rawField.pendingBoundary;
        const field: Field = {
          ...rawField,
          boundary: rawField.geometry ? JSON.stringify(rawField.geometry) : (rawField.boundary || pendingBoundary),
        };
        const farmId = field.farm_id;
        if (!state.fields[farmId]) {
          state.fields[farmId] = [];
        }
        state.fields[farmId].push(field);
      })
      .addCase(createField.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create field';
      });

    // Update Field
    builder
      .addCase(updateField.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateField.fulfilled, (state, action) => {
        state.loading = false;
        // Transform API response: geometry object → boundary string (same as fetchFields)
        const rawField: any = action.payload;
        console.log('🔄 updateField.fulfilled - Raw API response:', rawField);
        console.log('  Has geometry?', !!rawField.geometry);
        console.log('  Has boundary?', !!rawField.boundary);
        console.log('  geometry value:', rawField.geometry);
        console.log('  boundary value:', rawField.boundary);
        
        const field: Field = {
          ...rawField,
          boundary: rawField.geometry ? JSON.stringify(rawField.geometry) : rawField.boundary,
        };
        console.log('  Transformed boundary:', field.boundary);
        
        const farmId = field.farm_id;
        const farmFields = state.fields[farmId];
        console.log('  Farm ID:', farmId);
        console.log('  Farm has fields?', !!farmFields);
        console.log('  Number of fields:', farmFields?.length);
        
        if (farmFields) {
          const index = farmFields.findIndex((f) => f.id === field.id);
          console.log('  Field index in array:', index);
          if (index !== -1) {
            farmFields[index] = field;
            console.log('  ✅ Field updated in Redux store');
          } else {
            console.error('  ❌ Field not found in farmFields array!');
          }
        } else {
          console.error('  ❌ No fields array found for farmId:', farmId);
        }
        
        if (state.selectedField?.id === field.id) {
          state.selectedField = field;
        }
      })
      .addCase(updateField.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update field';
      });

    // Delete Field
    builder
      .addCase(deleteField.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteField.fulfilled, (state, action) => {
        state.loading = false;
        Object.keys(state.fields).forEach((farmId) => {
          const farmFields = state.fields[farmId];
          if (farmFields) {
            state.fields[farmId] = farmFields.filter((f) => f.id !== action.payload);
          }
        });
        if (state.selectedField?.id === action.payload) {
          state.selectedField = null;
        }
      })
      .addCase(deleteField.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete field';
      });
  },
});

export const { selectField, clearSelectedField } = fieldSlice.actions;
export const fieldReducer = fieldSlice.reducer;

// Selectors
export const selectFieldsByFarm = (farmId: string) => (state: { fields: FieldState }) => 
  state.fields.fields[farmId] || emptyArray<Field>();
export const selectSelectedField = (state: { fields: FieldState }) => state.fields.selectedField;
export const selectFieldLoading = (state: { fields: FieldState }) => state.fields.loading;
export const selectFieldError = (state: { fields: FieldState }) => state.fields.error;

// ============================================================================
// Irrigation Sector Slice
// ============================================================================

interface IrrigationSectorState {
  sectors: Record<string, IrrigationSector[]>; // fieldId -> sectors
  selectedSector: IrrigationSector | null;
  loading: boolean;
  error: string | null;
}

const initialIrrigationSectorState: IrrigationSectorState = {
  sectors: {},
  selectedSector: null,
  loading: false,
  error: null,
};

// Async Thunks
export const fetchIrrigationSectors = createAsyncThunk(
  'irrigationSectors/fetchIrrigationSectors',
  async (params: { field_id: string; page?: number; size?: number }) => {
    const response = await farmApi.getIrrigationSectors(params);
    return { fieldId: params.field_id, sectors: response.items };
  }
);

export const fetchIrrigationSectorsByFarm = createAsyncThunk(
  'irrigationSectors/fetchIrrigationSectorsByFarm',
  async (params: { farm_id: string; page?: number; size?: number }) => {
    const key = `sectorsByFarm:${params.farm_id}`;
    markInFlight(key);
    try {
      const response = await farmApi.getIrrigationSectorsByFarm(params);
      return { farmId: params.farm_id, sectors: response.items };
    } finally {
      clearInFlight(key);
    }
  },
  {
    condition: (params: { farm_id: string; page?: number; size?: number }) =>
      !isInFlight(`sectorsByFarm:${params.farm_id}`),
  }
);

export const createIrrigationSector = createAsyncThunk(
  'irrigationSectors/createIrrigationSector',
  async (data: IrrigationSectorCreate) => {
    const response = await farmApi.createIrrigationSector(data);
    return response;
  }
);

export const updateIrrigationSector = createAsyncThunk(
  'irrigationSectors/updateIrrigationSector',
  async ({ id, data }: { id: string; data: IrrigationSectorUpdate }) => {
    const response = await farmApi.updateIrrigationSector(id, data);
    return response;
  }
);

export const deleteIrrigationSector = createAsyncThunk(
  'irrigationSectors/deleteIrrigationSector',
  async (id: string) => {
    await farmApi.deleteIrrigationSector(id);
    return id;
  }
);

// Slice
const irrigationSectorSlice = createSlice({
  name: 'irrigationSectors',
  initialState: initialIrrigationSectorState,
  reducers: {
    selectSector: (state, action: PayloadAction<IrrigationSector>) => {
      state.selectedSector = action.payload;
    },
    clearSelectedSector: (state) => {
      state.selectedSector = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Irrigation Sectors
    builder
      .addCase(fetchIrrigationSectors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIrrigationSectors.fulfilled, (state, action) => {
        state.loading = false;
        // Transform API response: geometry object → boundary string
        const transformedSectors = action.payload.sectors.map((sector: any) => ({
          ...sector,
          boundary: sector.geometry || sector.boundary,
        }));
        state.sectors[action.payload.fieldId] = transformedSectors;
      })
      .addCase(fetchIrrigationSectors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch irrigation sectors';
      });

    // Fetch Irrigation Sectors by Farm
    builder
      .addCase(fetchIrrigationSectorsByFarm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIrrigationSectorsByFarm.fulfilled, (state, action) => {
        state.loading = false;
        // Group sectors by field_id and store in state
        const sectorsByField: Record<string, IrrigationSector[]> = {};
        action.payload.sectors.forEach((sector: any) => {
          const fieldId = sector.field_id;
          if (!sectorsByField[fieldId]) {
            sectorsByField[fieldId] = [];
          }
          // Transform API response: geometry object → boundary string
          const boundary = sector.geometry || sector.boundary;
          sectorsByField[fieldId].push({
            ...sector,
            boundary,
          });
        });
        
        // Update state for each field
        Object.keys(sectorsByField).forEach((fieldId) => {
          state.sectors[fieldId] = sectorsByField[fieldId];
        });
      })
      .addCase(fetchIrrigationSectorsByFarm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch irrigation sectors';
      });

    // Create Irrigation Sector
    builder
      .addCase(createIrrigationSector.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createIrrigationSector.fulfilled, (state, action) => {
        state.loading = false;
        const sector: any = action.payload;
        const fieldId = sector.field_id;
        if (!state.sectors[fieldId]) {
          state.sectors[fieldId] = [];
        }
        // Mirror the fetch transform so stored shape is identical regardless of
        // whether the sector arrived via GET-list or POST.
        state.sectors[fieldId].push({
          ...sector,
          boundary: sector.geometry || sector.boundary,
        });
      })
      .addCase(createIrrigationSector.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create irrigation sector';
      });

    // Update Irrigation Sector
    builder
      .addCase(updateIrrigationSector.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateIrrigationSector.fulfilled, (state, action) => {
        state.loading = false;
        const raw: any = action.payload;
        // Mirror the fetch transform (geometry → boundary) so an updated sector
        // has the same shape as a fetched one; otherwise the map's geometry
        // comparison could miss the change.
        const sector = { ...raw, boundary: raw.geometry || raw.boundary };
        const fieldId = sector.field_id;
        const fieldSectors = state.sectors[fieldId];
        if (fieldSectors) {
          const index = fieldSectors.findIndex((s) => s.id === sector.id);
          if (index !== -1) {
            fieldSectors[index] = sector;
          }
        }
        if (state.selectedSector?.id === sector.id) {
          state.selectedSector = sector;
        }
      })
      .addCase(updateIrrigationSector.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update irrigation sector';
      });

    // Delete Irrigation Sector
    builder
      .addCase(deleteIrrigationSector.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteIrrigationSector.fulfilled, (state, action) => {
        state.loading = false;
        Object.keys(state.sectors).forEach((fieldId) => {
          const fieldSectors = state.sectors[fieldId];
          if (fieldSectors) {
            state.sectors[fieldId] = fieldSectors.filter((s) => s.id !== action.payload);
          }
        });
        if (state.selectedSector?.id === action.payload) {
          state.selectedSector = null;
        }
      })
      .addCase(deleteIrrigationSector.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete irrigation sector';
      });
  },
});

export const { selectSector, clearSelectedSector } = irrigationSectorSlice.actions;
export const irrigationSectorReducer = irrigationSectorSlice.reducer;

// Selectors
export const selectSectorsByField = (fieldId: string) => (state: { irrigationSectors: IrrigationSectorState }) =>
  state.irrigationSectors.sectors[fieldId] || emptyArray<IrrigationSector>();

// Derived setup-progress selectors (used by the onboarding checklist)
export const selectHasAnyField = createSelector(
  [(state: { fields: FieldState }) => state.fields.fields],
  (fieldsByFarm) => Object.values(fieldsByFarm).some((fields) => fields.length > 0)
);

export const selectHasAnySector = createSelector(
  [(state: { irrigationSectors: IrrigationSectorState }) => state.irrigationSectors.sectors],
  (sectorsByField) => Object.values(sectorsByField).some((sectors) => sectors.length > 0)
);

export const selectHasAnyValveAssigned = createSelector(
  [(state: { irrigationSectors: IrrigationSectorState }) => state.irrigationSectors.sectors],
  (sectorsByField) =>
    Object.values(sectorsByField).some((sectors) => sectors.some((sector) => Boolean(sector.valve_id)))
);
export const selectSelectedSector = (state: { irrigationSectors: IrrigationSectorState }) => state.irrigationSectors.selectedSector;
export const selectIrrigationSectorLoading = (state: { irrigationSectors: IrrigationSectorState }) => state.irrigationSectors.loading;
export const selectIrrigationSectorError = (state: { irrigationSectors: IrrigationSectorState }) => state.irrigationSectors.error;

// ============================================================================
// Pump Station Slice
// ============================================================================

interface PumpStationState {
  pumpStations: Record<string, PumpStation[]>; // farmId -> pump stations
  selectedPumpStation: PumpStation | null;
  loading: boolean;
  error: string | null;
}

const initialPumpStationState: PumpStationState = {
  pumpStations: {},
  selectedPumpStation: null,
  loading: false,
  error: null,
};

// Async Thunks
export const fetchPumpStations = createAsyncThunk(
  'pumpStations/fetchPumpStations',
  async (params: { farm_id: string; page?: number; size?: number }) => {
    const response = await farmApi.getPumpStations(params);
    return { farmId: params.farm_id, pumpStations: response.items };
  }
);

export const createPumpStation = createAsyncThunk(
  'pumpStations/createPumpStation',
  async (data: PumpStationCreate) => {
    const response = await farmApi.createPumpStation(data);
    return response;
  }
);

export const updatePumpStation = createAsyncThunk(
  'pumpStations/updatePumpStation',
  async ({ id, data }: { id: string; data: PumpStationUpdate }) => {
    const response = await farmApi.updatePumpStation(id, data);
    return response;
  }
);

export const deletePumpStation = createAsyncThunk(
  'pumpStations/deletePumpStation',
  async (id: string) => {
    await farmApi.deletePumpStation(id);
    return id;
  }
);

// Slice
const pumpStationSlice = createSlice({
  name: 'pumpStations',
  initialState: initialPumpStationState,
  reducers: {
    selectPumpStation: (state, action: PayloadAction<PumpStation>) => {
      state.selectedPumpStation = action.payload;
    },
    clearSelectedPumpStation: (state) => {
      state.selectedPumpStation = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Pump Stations
    builder
      .addCase(fetchPumpStations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPumpStations.fulfilled, (state, action) => {
        state.loading = false;
        state.pumpStations[action.payload.farmId] = action.payload.pumpStations;
      })
      .addCase(fetchPumpStations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch pump stations';
      });

    // Create Pump Station
    builder
      .addCase(createPumpStation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPumpStation.fulfilled, (state, action) => {
        state.loading = false;
        const farmId = action.payload.farm_id;
        if (!state.pumpStations[farmId]) {
          state.pumpStations[farmId] = [];
        }
        state.pumpStations[farmId].push(action.payload);
      })
      .addCase(createPumpStation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create pump station';
      });

    // Update Pump Station
    builder
      .addCase(updatePumpStation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePumpStation.fulfilled, (state, action) => {
        state.loading = false;
        const pumpStation = action.payload;
        const farmId = pumpStation.farm_id;
        const farmPumpStations = state.pumpStations[farmId];
        if (farmPumpStations) {
          const index = farmPumpStations.findIndex((p) => p.id === pumpStation.id);
          if (index !== -1) {
            farmPumpStations[index] = pumpStation;
          }
        }
        if (state.selectedPumpStation?.id === pumpStation.id) {
          state.selectedPumpStation = pumpStation;
        }
      })
      .addCase(updatePumpStation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update pump station';
      });

    // Delete Pump Station
    builder
      .addCase(deletePumpStation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePumpStation.fulfilled, (state, action) => {
        state.loading = false;
        Object.keys(state.pumpStations).forEach((farmId) => {
          const farmPumpStations = state.pumpStations[farmId];
          if (farmPumpStations) {
            state.pumpStations[farmId] = farmPumpStations.filter((p) => p.id !== action.payload);
          }
        });
        if (state.selectedPumpStation?.id === action.payload) {
          state.selectedPumpStation = null;
        }
      })
      .addCase(deletePumpStation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete pump station';
      });
  },
});

export const { selectPumpStation, clearSelectedPumpStation } = pumpStationSlice.actions;
export const pumpStationReducer = pumpStationSlice.reducer;

// Selectors
export const selectPumpStationsByFarm = (farmId: string) => (state: { pumpStations: PumpStationState }) => 
  state.pumpStations.pumpStations[farmId] || emptyArray<PumpStation>();
export const selectSelectedPumpStation = (state: { pumpStations: PumpStationState }) => state.pumpStations.selectedPumpStation;
export const selectPumpStationLoading = (state: { pumpStations: PumpStationState }) => state.pumpStations.loading;
export const selectPumpStationError = (state: { pumpStations: PumpStationState }) => state.pumpStations.error;

// ============================================================================
// Weather Station Slice
// ============================================================================

interface WeatherStationState {
  weatherStations: Record<string, WeatherStation[]>; // farmId -> weather stations
  selectedWeatherStation: WeatherStation | null;
  loading: boolean;
  error: string | null;
}

const initialWeatherStationState: WeatherStationState = {
  weatherStations: {},
  selectedWeatherStation: null,
  loading: false,
  error: null,
};

// Async Thunks
export const fetchWeatherStations = createAsyncThunk(
  'weatherStations/fetchWeatherStations',
  async (params: { farm_id: string; page?: number; size?: number }) => {
    const response = await farmApi.getWeatherStations(params);
    return { farmId: params.farm_id, weatherStations: response.items };
  }
);

export const createWeatherStation = createAsyncThunk(
  'weatherStations/createWeatherStation',
  async (data: WeatherStationCreate) => {
    const response = await farmApi.createWeatherStation(data);
    return response;
  }
);

export const updateWeatherStation = createAsyncThunk(
  'weatherStations/updateWeatherStation',
  async ({ id, data }: { id: string; data: WeatherStationUpdate }) => {
    const response = await farmApi.updateWeatherStation(id, data);
    return response;
  }
);

export const deleteWeatherStation = createAsyncThunk(
  'weatherStations/deleteWeatherStation',
  async (id: string) => {
    await farmApi.deleteWeatherStation(id);
    return id;
  }
);

export const fetchWeatherStationData = createAsyncThunk(
  'weatherStations/fetchWeatherStationData',
  async (stationId: string) => {
    const response = await farmApi.getWeatherStationData(stationId);
    return response;
  }
);

// Slice
const weatherStationSlice = createSlice({
  name: 'weatherStations',
  initialState: initialWeatherStationState,
  reducers: {
    selectWeatherStation: (state, action: PayloadAction<WeatherStation>) => {
      state.selectedWeatherStation = action.payload;
    },
    clearSelectedWeatherStation: (state) => {
      state.selectedWeatherStation = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Weather Stations
    builder
      .addCase(fetchWeatherStations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWeatherStations.fulfilled, (state, action) => {
        state.loading = false;
        state.weatherStations[action.payload.farmId] = action.payload.weatherStations;
      })
      .addCase(fetchWeatherStations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch weather stations';
      });

    // Create Weather Station
    builder
      .addCase(createWeatherStation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createWeatherStation.fulfilled, (state, action) => {
        state.loading = false;
        const farmId = action.payload.farm_id;
        if (!state.weatherStations[farmId]) {
          state.weatherStations[farmId] = [];
        }
        state.weatherStations[farmId].push(action.payload);
      })
      .addCase(createWeatherStation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create weather station';
      });

    // Update Weather Station
    builder
      .addCase(updateWeatherStation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateWeatherStation.fulfilled, (state, action) => {
        state.loading = false;
        const weatherStation = action.payload;
        const farmId = weatherStation.farm_id;
        const farmWeatherStations = state.weatherStations[farmId];
        if (farmWeatherStations) {
          const index = farmWeatherStations.findIndex((w) => w.id === weatherStation.id);
          if (index !== -1) {
            farmWeatherStations[index] = weatherStation;
          }
        }
        if (state.selectedWeatherStation?.id === weatherStation.id) {
          state.selectedWeatherStation = weatherStation;
        }
      })
      .addCase(updateWeatherStation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update weather station';
      });

    // Delete Weather Station
    builder
      .addCase(deleteWeatherStation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteWeatherStation.fulfilled, (state, action) => {
        state.loading = false;
        Object.keys(state.weatherStations).forEach((farmId) => {
          const farmWeatherStations = state.weatherStations[farmId];
          if (farmWeatherStations) {
            state.weatherStations[farmId] = farmWeatherStations.filter((w) => w.id !== action.payload);
          }
        });
        if (state.selectedWeatherStation?.id === action.payload) {
          state.selectedWeatherStation = null;
        }
      })
      .addCase(deleteWeatherStation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete weather station';
      });

    // Fetch Weather Station Data
    builder
      .addCase(fetchWeatherStationData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWeatherStationData.fulfilled, (state) => {
        state.loading = false;
        // Weather data is stored separately in farm slice
      })
      .addCase(fetchWeatherStationData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch weather data';
      });
  },
});

export const { selectWeatherStation, clearSelectedWeatherStation } = weatherStationSlice.actions;
export const weatherStationReducer = weatherStationSlice.reducer;

// Selectors
export const selectWeatherStationsByFarm = (farmId: string) => (state: { weatherStations: WeatherStationState }) =>
  state.weatherStations.weatherStations[farmId] || emptyArray<WeatherStation>();
export const selectSelectedWeatherStation = (state: { weatherStations: WeatherStationState }) => state.weatherStations.selectedWeatherStation;
export const selectWeatherStationLoading = (state: { weatherStations: WeatherStationState }) => state.weatherStations.loading;
export const selectWeatherStationError = (state: { weatherStations: WeatherStationState }) => state.weatherStations.error;

// ============================================================================
// Irrigation Zone Slice
// ============================================================================

interface IrrigationZoneState {
  zones: IrrigationZone[];
  zoneSectorMemberships: ZoneSectorMembership[];
  zonesLoading: boolean;
  zonesError: string | null;
}

const initialIrrigationZoneState: IrrigationZoneState = {
  zones: [],
  zoneSectorMemberships: [],
  zonesLoading: false,
  zonesError: null,
};

// Async Thunks
export const fetchZones = createAsyncThunk(
  'irrigationZones/fetchZones',
  async (params: { farmId: string; includeInactive?: boolean }) => {
    const response = await irrigationZonesApi.getIrrigationZones(
      params.farmId,
      {
        include_inactive: params.includeInactive,
      }
    );
    return { farmId: params.farmId, zones: response.items };
  }
);

export const fetchZoneSectorMemberships = createAsyncThunk(
  'irrigationZones/fetchZoneSectorMemberships',
  async (params: { farmId: string }) => {
    const response = await irrigationZonesApi.getZoneSectorMembershipsForFarm(params.farmId);
    return { farmId: params.farmId, memberships: response };
  }
);

export const createZone = createAsyncThunk(
  'irrigationZones/createZone',
  async (params: { zoneData: IrrigationZoneCreate; sectorIds?: number[] }) => {
    // Create the zone first
    const zone = await irrigationZonesApi.createIrrigationZone(params.zoneData);
    
    // If sectors are provided, add them to the zone
    if (params.sectorIds && params.sectorIds.length > 0) {
      const memberships: ZoneSectorMembership[] = [];
      for (const sectorId of params.sectorIds) {
        const membership = await irrigationZonesApi.addSectorToZone(zone.id, sectorId);
        memberships.push(membership);
      }
      return { zone, memberships };
    }
    
    return { zone, memberships: [] };
  }
);

export const updateZone = createAsyncThunk(
  'irrigationZones/updateZone',
  async ({ zoneId, data }: { zoneId: number; data: IrrigationZoneUpdate }) => {
    const response = await irrigationZonesApi.updateIrrigationZone(zoneId, data);
    return response;
  }
);

export const deleteZone = createAsyncThunk(
  'irrigationZones/deleteZone',
  async (zoneId: number) => {
    await irrigationZonesApi.deleteIrrigationZone(zoneId);
    return zoneId;
  }
);

export const addSectorToZone = createAsyncThunk(
  'irrigationZones/addSectorToZone',
  async ({ zoneId, sectorId }: { zoneId: number; sectorId: number }) => {
    const response = await irrigationZonesApi.addSectorToZone(zoneId, sectorId);
    return response;
  }
);

export const removeSectorFromZone = createAsyncThunk(
  'irrigationZones/removeSectorFromZone',
  async (membershipId: number) => {
    await irrigationZonesApi.removeSectorFromZone(membershipId);
    return membershipId;
  }
);

// Slice
const irrigationZoneSlice = createSlice({
  name: 'irrigationZones',
  initialState: initialIrrigationZoneState,
  reducers: {
    clearZonesError: (state) => {
      state.zonesError = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Zones
    builder
      .addCase(fetchZones.pending, (state) => {
        state.zonesLoading = true;
        state.zonesError = null;
      })
      .addCase(fetchZones.fulfilled, (state, action) => {
        state.zonesLoading = false;
        // Filter out zones for other farms and replace/add zones for this farm
        state.zones = state.zones.filter((z) => z.farm_id !== action.payload.farmId);
        state.zones.push(...action.payload.zones);
      })
      .addCase(fetchZones.rejected, (state, action) => {
        state.zonesLoading = false;
        state.zonesError = action.error.message || 'Failed to fetch irrigation zones';
      });

    // Fetch Zone Sector Memberships
    builder
      .addCase(fetchZoneSectorMemberships.pending, (state) => {
        state.zonesLoading = true;
        state.zonesError = null;
      })
      .addCase(fetchZoneSectorMemberships.fulfilled, (state, action) => {
        state.zonesLoading = false;
        // Replace memberships for this farm
        state.zoneSectorMemberships = action.payload.memberships;
      })
      .addCase(fetchZoneSectorMemberships.rejected, (state, action) => {
        state.zonesLoading = false;
        state.zonesError = action.error.message || 'Failed to fetch zone sector memberships';
      });

    // Create Zone
    builder
      .addCase(createZone.pending, (state) => {
        state.zonesLoading = true;
        state.zonesError = null;
      })
      .addCase(createZone.fulfilled, (state, action) => {
        state.zonesLoading = false;
        state.zones.push(action.payload.zone);
        if (action.payload.memberships.length > 0) {
          state.zoneSectorMemberships.push(...action.payload.memberships);
        }
      })
      .addCase(createZone.rejected, (state, action) => {
        state.zonesLoading = false;
        state.zonesError = action.error.message || 'Failed to create irrigation zone';
      });

    // Update Zone
    builder
      .addCase(updateZone.pending, (state) => {
        state.zonesLoading = true;
        state.zonesError = null;
      })
      .addCase(updateZone.fulfilled, (state, action) => {
        state.zonesLoading = false;
        const index = state.zones.findIndex((z) => z.id === action.payload.id);
        if (index !== -1) {
          state.zones[index] = action.payload;
        }
      })
      .addCase(updateZone.rejected, (state, action) => {
        state.zonesLoading = false;
        state.zonesError = action.error.message || 'Failed to update irrigation zone';
      });

    // Delete Zone
    builder
      .addCase(deleteZone.pending, (state) => {
        state.zonesLoading = true;
        state.zonesError = null;
      })
      .addCase(deleteZone.fulfilled, (state, action) => {
        state.zonesLoading = false;
        state.zones = state.zones.filter((z) => z.id !== action.payload);
        // Also remove related memberships
        state.zoneSectorMemberships = state.zoneSectorMemberships.filter(
          (m) => m.zone_id !== action.payload
        );
      })
      .addCase(deleteZone.rejected, (state, action) => {
        state.zonesLoading = false;
        state.zonesError = action.error.message || 'Failed to delete irrigation zone';
      });

    // Add Sector to Zone
    builder
      .addCase(addSectorToZone.pending, (state) => {
        state.zonesLoading = true;
        state.zonesError = null;
      })
      .addCase(addSectorToZone.fulfilled, (state, action) => {
        state.zonesLoading = false;
        state.zoneSectorMemberships.push(action.payload);
      })
      .addCase(addSectorToZone.rejected, (state, action) => {
        state.zonesLoading = false;
        state.zonesError = action.error.message || 'Failed to add sector to zone';
      });

    // Remove Sector from Zone
    builder
      .addCase(removeSectorFromZone.pending, (state) => {
        state.zonesLoading = true;
        state.zonesError = null;
      })
      .addCase(removeSectorFromZone.fulfilled, (state, action) => {
        state.zonesLoading = false;
        state.zoneSectorMemberships = state.zoneSectorMemberships.filter(
          (m) => m.id !== action.payload
        );
      })
      .addCase(removeSectorFromZone.rejected, (state, action) => {
        state.zonesLoading = false;
        state.zonesError = action.error.message || 'Failed to remove sector from zone';
      });
  },
});

export const { clearZonesError } = irrigationZoneSlice.actions;
export const irrigationZoneReducer = irrigationZoneSlice.reducer;

// Selectors
export const selectZonesByFarm = (farmId: string) => (state: { irrigationZones: IrrigationZoneState }) =>
  state.irrigationZones.zones.filter((z) => z.farm_id === farmId && z.is_active);
export const selectZoneById = (zoneId: number) => (state: { irrigationZones: IrrigationZoneState }) =>
  state.irrigationZones.zones.find((z) => z.id === zoneId);
export const selectSectorsByZone = (zoneId: number) => (state: { irrigationZones: IrrigationZoneState }) =>
  state.irrigationZones.zoneSectorMemberships.filter(
    (m) => m.zone_id === zoneId && !m.left_at
  );
/**
 * Every ACTIVE membership of a sector, lowest zone id first.
 *
 * Plural: a sector may belong to several zones. This was `selectZoneForSector`, a
 * `.find()` returning one membership — which cemented the one-zone assumption in the
 * UI layer and, because membership array order is not stable across refetches, did
 * not even return the same one consistently.
 */
export const selectZonesForSector = (sectorId: number) => (state: { irrigationZones: IrrigationZoneState }) =>
  state.irrigationZones.zoneSectorMemberships
    .filter((m) => m.sector_id === sectorId && !m.left_at)
    .sort((a, b) => a.zone_id - b.zone_id);
export const selectZonesLoading = (state: { irrigationZones: IrrigationZoneState }) =>
  state.irrigationZones.zonesLoading;
export const selectZonesError = (state: { irrigationZones: IrrigationZoneState }) =>
  state.irrigationZones.zonesError;

// ============================================================================
// Geo Import Slice
// ============================================================================

interface GeoImportState {
  preview: GeoJSON.FeatureCollection | null;
  importResult: GeoImportResult | null;
  loading: boolean;
  error: string | null;
}

const initialGeoImportState: GeoImportState = {
  preview: null,
  importResult: null,
  loading: false,
  error: null,
};

// Async Thunks
export const previewGeoDataAction = createAsyncThunk(
  'geoImport/previewGeoData',
  async (formData: FormData) => {
    const response = await farmApi.previewGeoData(formData);
    return response;
  }
);

export const importGeoDataAction = createAsyncThunk(
  'geoImport/importGeoData',
  async (formData: FormData) => {
    const response = await farmApi.importGeoData(formData);
    return response;
  }
);

// Slice
const geoImportSlice = createSlice({
  name: 'geoImport',
  initialState: initialGeoImportState,
  reducers: {
    clearGeoImport: (state) => {
      state.preview = null;
      state.importResult = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Preview Geo Data
    builder
      .addCase(previewGeoDataAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(previewGeoDataAction.fulfilled, (state, action) => {
        state.loading = false;
        state.preview = action.payload;
      })
      .addCase(previewGeoDataAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to preview geo data';
      });

    // Import Geo Data
    builder
      .addCase(importGeoDataAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(importGeoDataAction.fulfilled, (state, action) => {
        state.loading = false;
        state.importResult = action.payload;
      })
      .addCase(importGeoDataAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to import geo data';
      });
  },
});

export const { clearGeoImport } = geoImportSlice.actions;
export const geoImportReducer = geoImportSlice.reducer;

// Selectors
export const selectGeoImportPreview = (state: { geoImport: GeoImportState }) => state.geoImport.preview;
export const selectGeoImportResult = (state: { geoImport: GeoImportState }) => state.geoImport.importResult;
export const selectGeoImportLoading = (state: { geoImport: GeoImportState }) => state.geoImport.loading;
export const selectGeoImportError = (state: { geoImport: GeoImportState }) => state.geoImport.error;

// ============================================================================
// Layout Editor Slice
// ============================================================================

interface LayoutEditorState {
  viewMode: 'fields' | 'sectors';
  selectedItemType: 'none' | 'field' | 'sector' | 'device';
  selectedItemId: string | null;
  isEditing: boolean;
  isEditingBoundary: boolean;
  isDrawing: boolean;
  pendingBoundary: string | null; // GeoJSON string
}

const initialLayoutEditorState: LayoutEditorState = {
  viewMode: 'fields',
  selectedItemType: 'none',
  selectedItemId: null,
  isEditing: false,
  isEditingBoundary: false,
  isDrawing: false,
  pendingBoundary: null,
};

// Slice
const layoutEditorSlice = createSlice({
  name: 'layoutEditor',
  initialState: initialLayoutEditorState,
  reducers: {
    setViewMode: (state, action: PayloadAction<'fields' | 'sectors'>) => {
      state.viewMode = action.payload;
    },
    selectItem: (state, action: PayloadAction<{ type: 'none' | 'field' | 'sector' | 'device'; id: string | null }>) => {
      state.selectedItemType = action.payload.type;
      state.selectedItemId = action.payload.id;
    },
    clearSelection: (state) => {
      state.selectedItemType = 'none';
      state.selectedItemId = null;
    },
    setEditMode: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    setEditingBoundary: (state, action: PayloadAction<boolean>) => {
      state.isEditingBoundary = action.payload;
    },
    setDrawMode: (state, action: PayloadAction<boolean>) => {
      state.isDrawing = action.payload;
    },
    setPendingBoundary: (state, action: PayloadAction<string | null>) => {
      state.pendingBoundary = action.payload;
    },
  },
});

export const {
  setViewMode,
  selectItem,
  clearSelection,
  setEditMode,
  setEditingBoundary,
  setDrawMode,
  setPendingBoundary,
} = layoutEditorSlice.actions;
export const layoutEditorReducer = layoutEditorSlice.reducer;

// Selectors
export const selectLayoutEditorViewMode = (state: { layoutEditor: LayoutEditorState }) => state.layoutEditor.viewMode;
export const selectLayoutEditorSelectedItemType = (state: { layoutEditor: LayoutEditorState }) => state.layoutEditor.selectedItemType;
export const selectLayoutEditorSelectedItemId = (state: { layoutEditor: LayoutEditorState }) => state.layoutEditor.selectedItemId;
export const selectLayoutEditorIsEditing = (state: { layoutEditor: LayoutEditorState }) => state.layoutEditor.isEditing;
export const selectLayoutEditorIsEditingBoundary = (state: { layoutEditor: LayoutEditorState }) => state.layoutEditor.isEditingBoundary;
export const selectLayoutEditorIsDrawing = (state: { layoutEditor: LayoutEditorState }) => state.layoutEditor.isDrawing;
export const selectLayoutEditorPendingBoundary = (state: { layoutEditor: LayoutEditorState }) => state.layoutEditor.pendingBoundary;

// ============================================================================
// Table Editor Slice
// ============================================================================

interface TableEditorState {
  fieldsDirty: string[]; // Array of field IDs with unsaved changes
  sectorsDirty: string[]; // Array of sector IDs with unsaved changes
  fieldSaveStatus: Record<string, 'idle' | 'saving' | 'success' | 'error'>;
  sectorSaveStatus: Record<string, 'idle' | 'saving' | 'success' | 'error'>;
  fieldErrors: Record<string, Record<string, string>>; // fieldId -> fieldKey -> errorMessage
  sectorErrors: Record<string, Record<string, string>>; // sectorId -> fieldKey -> errorMessage
}

const initialTableEditorState: TableEditorState = {
  fieldsDirty: [],
  sectorsDirty: [],
  fieldSaveStatus: {},
  sectorSaveStatus: {},
  fieldErrors: {},
  sectorErrors: {},
};

// Slice
const tableEditorSlice = createSlice({
  name: 'tableEditor',
  initialState: initialTableEditorState,
  reducers: {
    // Field dirty state reducers
    setFieldDirty: (state, action: PayloadAction<{id: string, isDirty: boolean}>) => {
      const { id, isDirty } = action.payload;
      if (isDirty) {
        if (!state.fieldsDirty.includes(id)) {
          state.fieldsDirty.push(id);
        }
      } else {
        state.fieldsDirty = state.fieldsDirty.filter(fieldId => fieldId !== id);
      }
    },
    clearFieldDirty: (state) => {
      state.fieldsDirty = [];
    },
    setFieldSaveStatus: (state, action: PayloadAction<{id: string, status: 'idle' | 'saving' | 'success' | 'error'}>) => {
      state.fieldSaveStatus[action.payload.id] = action.payload.status;
    },
    setFieldError: (state, action: PayloadAction<{id: string, fieldKey: string, message: string}>) => {
      const { id, fieldKey, message } = action.payload;
      if (!state.fieldErrors[id]) {
        state.fieldErrors[id] = {};
      }
      state.fieldErrors[id][fieldKey] = message;
    },
    clearFieldError: (state, action: PayloadAction<{id: string, fieldKey?: string}>) => {
      const { id, fieldKey } = action.payload;
      if (fieldKey) {
        delete state.fieldErrors[id]?.[fieldKey];
      } else {
        delete state.fieldErrors[id];
      }
    },
    // Sector dirty state reducers
    setSectorDirty: (state, action: PayloadAction<{id: string, isDirty: boolean}>) => {
      const { id, isDirty } = action.payload;
      if (isDirty) {
        if (!state.sectorsDirty.includes(id)) {
          state.sectorsDirty.push(id);
        }
      } else {
        state.sectorsDirty = state.sectorsDirty.filter(sectorId => sectorId !== id);
      }
    },
    clearSectorDirty: (state) => {
      state.sectorsDirty = [];
    },
    setSectorSaveStatus: (state, action: PayloadAction<{id: string, status: 'idle' | 'saving' | 'success' | 'error'}>) => {
      state.sectorSaveStatus[action.payload.id] = action.payload.status;
    },
    setSectorError: (state, action: PayloadAction<{id: string, fieldKey: string, message: string}>) => {
      const { id, fieldKey, message } = action.payload;
      if (!state.sectorErrors[id]) {
        state.sectorErrors[id] = {};
      }
      state.sectorErrors[id][fieldKey] = message;
    },
    clearSectorError: (state, action: PayloadAction<{id: string, fieldKey?: string}>) => {
      const { id, fieldKey } = action.payload;
      if (fieldKey) {
        delete state.sectorErrors[id]?.[fieldKey];
      } else {
        delete state.sectorErrors[id];
      }
    },
  },
});

export const {
  setFieldDirty,
  clearFieldDirty,
  setFieldSaveStatus,
  setFieldError,
  clearFieldError,
  setSectorDirty,
  clearSectorDirty,
  setSectorSaveStatus,
  setSectorError,
  clearSectorError,
} = tableEditorSlice.actions;
export const tableEditorReducer = tableEditorSlice.reducer;

// Selectors
export const selectFieldsDirty = (state: { tableEditor: TableEditorState }) =>
  state.tableEditor.fieldsDirty;

export const selectFieldSaveStatus = (fieldId: string) => (state: { tableEditor: TableEditorState }) =>
  state.tableEditor.fieldSaveStatus[fieldId] || 'idle';

export const selectFieldErrors = (fieldId: string) => (state: { tableEditor: TableEditorState }) =>
  state.tableEditor.fieldErrors[fieldId] || emptyRecord<Record<string, string>>();

export const selectSectorsDirty = (state: { tableEditor: TableEditorState }) =>
  state.tableEditor.sectorsDirty;

export const selectSectorSaveStatus = (sectorId: string) => (state: { tableEditor: TableEditorState }) =>
  state.tableEditor.sectorSaveStatus[sectorId] || 'idle';

export const selectSectorErrors = (sectorId: string) => (state: { tableEditor: TableEditorState }) =>
  state.tableEditor.sectorErrors[sectorId] || emptyRecord<Record<string, string>>();
